import React from 'react';
import { useXP } from '../../hooks/use-xp';
import styles from './XPDisplay.module.scss';

export interface XPDisplayProps {
  size?: 'small' | 'medium' | 'large';
  showLevel?: boolean;
  showProgress?: boolean;
  showXPText?: boolean;
  animated?: boolean;
  className?: string;
}

export const XPDisplay: React.FC<XPDisplayProps> = ({
  size = 'medium',
  showLevel = true,
  showProgress = true,
  showXPText = true,
  animated = true,
  className
}) => {
  const { xp, level, nextLevelXP, currentLevelProgress, getXPForNextLevel } = useXP();

  const currentLevelXP = xp - (level - 1) * 1000; // Simplified calculation for display
  const xpToNextLevel = getXPForNextLevel();

  return (
    <div 
      className={`${styles.xpDisplay} ${styles[size]} ${animated ? styles.animated : ''} ${className || ''}`}
      data-testid="xp-display"
    >
      {showLevel && (
        <div className={styles.level} data-testid="xp-level">
          <span className={styles.levelIcon}>⭐</span>
          <span className={styles.levelText}>Level {level}</span>
        </div>
      )}
      
      {showProgress && (
        <div className={styles.progressContainer}>
          <div className={styles.progressBar} data-testid="xp-progress-bar">
            <div 
              className={styles.progressFill}
              style={{ width: `${currentLevelProgress * 100}%` }}
              data-testid="xp-progress-fill"
            />
            {animated && (
              <div 
                className={styles.progressShine}
                style={{ left: `${currentLevelProgress * 100}%` }}
              />
            )}
          </div>
        </div>
      )}
      
      {showXPText && (
        <div className={styles.xpText} data-testid="xp-text">
          <span className={styles.currentXP}>{Math.floor(currentLevelXP)}</span>
          <span className={styles.separator}> / </span>
          <span className={styles.targetXP}>{nextLevelXP}</span>
          <span className={styles.label}> XP</span>
        </div>
      )}

      {/* Tooltip with detailed info */}
      <div className={styles.tooltip} data-testid="xp-tooltip">
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipRow}>
            <strong>Total XP:</strong> {xp.toLocaleString()}
          </div>
          <div className={styles.tooltipRow}>
            <strong>Current Level:</strong> {level}
          </div>
          <div className={styles.tooltipRow}>
            <strong>XP to Next Level:</strong> {xpToNextLevel}
          </div>
          <div className={styles.tooltipRow}>
            <strong>Progress:</strong> {Math.round(currentLevelProgress * 100)}%
          </div>
        </div>
      </div>
    </div>
  );
};